import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from '../../abstract/api.service';
import { AcreagePdfExportRequest } from '../request-models/acreage-pdf-export-request.model';
import { AcreagePdfExportResponse } from '../response-models/acreage-pdf-export-response.model';
import { AcreageReportFilterOptions } from '../response-models/acreage-report-filter-options.model';
import { AcreageReportOrgDetail } from '../response-models/acreage-report-org-detail.model';
import { AcreageReportSummary } from '../response-models/acreage-report-summary.model';
import {
  reportOrNull,
  toAcreageFilterOptions,
  toAcreageReportOrgDetail,
  toAcreageReportSummary,
  toPdfExportResponse,
} from '../utils/report-wire-utils';

/**
 * Acreage reports: `/admin/acreage-reports` (hub `admin-acreage-reports-controller.ts`). Dates are `YYYY-MM-DD`.
 * Failures reject with an `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class AcreageReportService extends ApiService {
  constructor() {
    super('admin/acreage-reports');
  }

  /** GET /admin/acreage-reports/organization/:organizationId, optionally scoped to one drone. */
  getOrganizationDetail(organizationId: string, startDate: string, endDate: string, droneId?: string): Promise<AcreageReportOrgDetail | null> {
    const opts = { path: ['organization', organizationId], query: { startDate, endDate, droneId: droneId || undefined } };
    return firstValueFrom(this.request('GET', opts, data => reportOrNull(data, toAcreageReportOrgDetail)));
  }

  /** GET /admin/acreage-reports/summary: every organization's totals. */
  getSummary(startDate: string, endDate: string): Promise<AcreageReportSummary | null> {
    const opts = { path: 'summary', query: { startDate, endDate } };
    return firstValueFrom(this.request('GET', opts, data => reportOrNull(data, toAcreageReportSummary)));
  }

  /** GET /admin/acreage-reports/filters: the organizations with data and the date range hints. */
  getFilters(): Promise<AcreageReportFilterOptions> {
    return firstValueFrom(this.request('GET', { path: 'filters' }, data => reportOrNull(data, toAcreageFilterOptions) ?? emptyFilters()));
  }

  /**
   * POST /admin/acreage-reports/export-pdf. The hub renders the PDF, stores it in S3 and returns a pre-signed
   * link, so this is a JSON call rather than a `download()`.
   */
  exportPdf(
    reportType: AcreagePdfExportRequest['reportType'],
    filters: AcreagePdfExportRequest['filters'],
    data: AcreagePdfExportRequest['data']
  ): Promise<AcreagePdfExportResponse | null> {
    const body: AcreagePdfExportRequest = { reportType, filters, data };
    return firstValueFrom(this.request('POST', { path: 'export-pdf' }, result => reportOrNull(result, toPdfExportResponse), body));
  }
}

function emptyFilters(): AcreageReportFilterOptions {
  return { organizations: [], dateRangeHints: { minDate: null, maxDate: null } };
}
